import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingIcon } from "../../assets/icons";
import { getPointApprovalRequests } from "../../service/query/auth";

const actionStyles = {
    reversal: "text-[#212121]",
    debit: "text-[#E02424]",
    credit: "text-[#0E9F6E]",
};

const ActionBadge = ({ action }) => (
    <span className={`inline-flex items-center gap-1 text-[13px] font-medium ${actionStyles[action] ?? "text-[#212121]"}`}>
        <span className="w-[6px] h-[6px] rounded-full bg-current" />
        {action?.charAt(0).toUpperCase() + action?.slice(1)}
    </span>
);

export default function PointsApprovals() {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const res = await getPointApprovalRequests({});
                setRequests(res?.data?.data ?? []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchRequests();
    }, []);

    const pending = requests.filter((r) => r.status === "PENDING");
    const credits = pending.filter((r) => r.action === "credit");
    const debits = pending.filter((r) => r.action === "debit");
    const reversals = pending.filter((r) => r.action === "reversal");

    const stats = [
        { label: "Pending approval", value: pending.length },
        { label: "Credits", value: credits.length },
        { label: "Debits", value: debits.length },
        { label: "Reversals", value: reversals.length },
    ];

    return (
        <div className="p-6 bg-white min-h-[80vh]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {stats.map((s) => (
                    <div key={s.label} className="border border-[#EAECF0] rounded-xl p-4">
                        <p className="text-[13px] text-[#667085] mb-1">{s.label}</p>
                        <p className="text-[28px] font-semibold text-[#212121]">{s.value}</p>
                    </div>
                ))}
            </div>

            <h4 className="text-[14px] font-semibold text-[#212121] mb-4">Pending requests</h4>

            {loading ? (
                <div className="flex justify-center py-10">
                    <LoadingIcon />
                </div>
            ) : (
                <div className="rounded-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-[#F8FAFC] h-[44px]">
                                <tr>
                                    <th className="px-4 py-3 text-black text-[12px] font-semibold border-b whitespace-nowrap">Request ID ↓</th>
                                    <th className="px-4 py-3 text-black text-[12px] font-semibold border-b whitespace-nowrap">Membership ID ↓</th>
                                    <th className="px-4 py-3 text-black text-[12px] font-semibold border-b whitespace-nowrap">Action ↓</th>
                                    <th className="px-4 py-3 text-black text-[12px] font-semibold border-b whitespace-nowrap">Points ↓</th>
                                    <th className="px-4 py-3 text-black text-[12px] font-semibold border-b whitespace-nowrap">Maker ↓</th>
                                    <th className="px-4 py-3 text-black text-[12px] font-semibold border-b whitespace-nowrap">Submitted ↓</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-10 text-gray-400">
                                            No pending requests
                                        </td>
                                    </tr>
                                ) : (
                                    pending.map((r) => (
                                        <tr
                                            key={r.id}
                                            className="border-b h-[60px] hover:bg-blue-50 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/points-approvals/${r.id}`)}
                                        >
                                            <td className="px-4 py-3 text-[#64748B] text-[14px]">{r.id}</td>
                                            <td className="px-4 py-3 text-[#64748B] text-[14px]">{r.membership_id}</td>
                                            <td className="px-4 py-3">
                                                <ActionBadge action={r.action} />
                                            </td>
                                            <td className="px-4 py-3 text-[#64748B] text-[14px]">
                                                {Number(r.points).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 text-[#64748B] text-[14px]">
                                                {r.maker?.First_name} {r.maker?.Last_name?.charAt(0)}.
                                            </td>
                                            <td className="px-4 py-3 text-[#64748B] text-[14px]">
                                                {new Date(r.created_at).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}